import React from "react";
import { createPortal } from "react-dom";
import "./AdRegisterLoadingModal.css";

export const DEFAULT_STEPS = [
  {
    id: "fetch",
    pendingLabel: "상품 및 스토어 정보 가져오기",
    activeLabel: "상품 및 스토어 정보 가져오는 중",
    doneLabel: "상품 및 스토어 정보 가져오기 완료",
  },
  {
    id: "keywords",
    pendingLabel: "추천 유입키워드 자동 생성",
    activeLabel: "추천 유입키워드 자동 생성 중",
    doneLabel: "추천 유입키워드 자동 생성 완료",
  },
  {
    id: "rank",
    pendingLabel: "추천 유입키워드 순위 조회",
    activeLabel: "추천 유입키워드 순위 조회 중",
    doneLabel: "추천 유입키워드 순위 조회 완료",
  },
];

const getStepLabel = (step, status) => {
  if (status === "done") {
    return step.doneLabel || step.label || "";
  }
  if (status === "active") {
    return step.activeLabel || step.label || "";
  }
  return step.pendingLabel || step.label || "";
};

/**
 * 광고등록 1페이지 → 다음 시 로딩 오버레이
 * - currentStep: 0~2 (진행 중 단계, 0-based)
 * - progress: 0~100
 * - batchHint: 3단계 배치 진행 문구
 * - 닫기/오버레이 클릭 불가
 */
const AdRegisterLoadingModal = ({
  open = false,
  steps = DEFAULT_STEPS,
  currentStep = 0,
  progress = 0,
  batchHint = "",
}) => {
  if (!open) return null;

  const safeProgress = Math.max(0, Math.min(100, Number(progress) || 0));
  const pctLabel = Math.round(safeProgress);

  return createPortal(
    <div
      className="arl-overlay"
      role="dialog"
      aria-modal="true"
      aria-busy="true"
    >
      <div className="arl-modal">
        <header className="arl-header">
          <p className="arl-eyebrow">광고 등록</p>
          <h2 className="arl-title">잠시만 기다려 주세요</h2>
          <p className="arl-sub">
            상품 정보를 준비하고 있어요.
            <br />
            완료되면 자동으로 다음 단계로 이동합니다.
          </p>
        </header>

        <div className="arl-warn" role="note">
          <span className="arl-warn-icon" aria-hidden="true">
            !
          </span>
          <div className="arl-warn-body">
            <strong>이 창은 닫을 수 없습니다</strong>
            <p>
              진행 중 다른 메뉴로 이동하거나 페이지를 벗어나면
              <br />
              <em>다시 광고를 등록해야 합니다.</em>
              <br />
              완료될 때까지 잠시만 기다려 주세요.
            </p>
          </div>
        </div>

        <div className="arl-gauge-block">
          <div className="arl-gauge-row">
            <span className="arl-gauge-label">진행률</span>
            <strong className="arl-gauge-pct">{pctLabel}%</strong>
          </div>
          <div
            className="arl-gauge-track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pctLabel}
          >
            <span
              className="arl-gauge-fill"
              style={{ width: `${safeProgress}%` }}
            />
          </div>
          {batchHint ? <p className="arl-batch-hint">{batchHint}</p> : null}
        </div>

        <ol className="arl-steps">
          {steps.map((step, index) => {
            let status = "pending";
            if (index < currentStep) status = "done";
            else if (index === currentStep) status = "active";
            if (safeProgress >= 100 && index <= currentStep) status = "done";

            return (
              <li key={step.id || index} className={`arl-step is-${status}`}>
                <span className="arl-step-icon" aria-hidden="true">
                  {status === "done" ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M3 7.2L5.8 10L11 3.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : status === "active" ? (
                    <span className="arl-spinner" />
                  ) : (
                    <span className="arl-dot" />
                  )}
                </span>
                <div className="arl-step-body">
                  <span className="arl-step-index">STEP {index + 1}</span>
                  <span className="arl-step-label">
                    {getStepLabel(step, status)}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>,
    document.body
  );
};

export default AdRegisterLoadingModal;
